// QuotationReview.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Base from "../../Config/Base";
import { Footer } from "../footer/footer";
import { API_BASE_URL } from "../../Config/Config";

// Pending = Quantity - TransferQty (already dispatched before this session)
const calcPending = (item) =>
  Math.max(0, (item.Quantity || 0) - (item.TransferQty || 0));

const today = () => new Date().toISOString().split("T")[0];

export default function QuotationReview() {
  const navigate = useNavigate();
  const { estimateId } = useParams();

  const [MisUserDetails, SetUserDetails] = useState({});
  const [quotationData, setQuotationData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // deliveryMap — { EstDetailId: { DeliveringQty: number } }
  const [deliveryMap, setDeliveryMap] = useState({});

  // mark-all checkbox
  const [markAll, setMarkAll] = useState(false);

  // delivery date (for payload)
  const [deliveryDate, setDeliveryDate] = useState(today());

  // ── session ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const details =
      JSON.parse(
        sessionStorage.getItem("MisUserDetails") ||
          localStorage.getItem("MisUserDetails"),
      ) || {};
    SetUserDetails(details);
  }, []);

  // ── fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!MisUserDetails?.OrgId || !MisUserDetails?.UserId) return;

    const fetchData = async () => {
      try {
        const url =
          `${API_BASE_URL}Billing/getEstimatesById` +
          `?Id=${estimateId}&OrgId=${MisUserDetails.OrgId}&UserId=${MisUserDetails.UserId}`;
        const res = await fetch(url);
        const data = await res.json();

        if (data?.Status && data.ResultData?.length > 0) {
          const first = data.ResultData[0];
          setQuotationData({
            Name: first.Name,
            Mobile: first.Mobile,
            Address: first.Address,
            BillNo: first.BillNo,
            Date: first.QuotationDate
              ? new Date(first.QuotationDate).toLocaleDateString("en-GB")
              : "—",
            Type: first.PaymentType,
            Total: first.Total,
            Advance: first.Advance,
            Notes: first.GoodsPolicy,
            Signatory: first.Signatory,
          });

          const mappedItems = data.ResultData.map((row) => ({
            EstDetailId: row.EstDetailId,
            SNo: row.SNo,
            Size: row.Size,
            Code: row.Code,
            ItemDesc: row.ItemDesc,
            Location: row.Location,
            Quantity: row.Quantity,
            TransferQty: row.TransferQty ?? 0,
            Unit: row.Unit,
            Boxes: row.Boxes ?? 0,
            ItemStatus: row.ItemStatus,
          }));

          // hide completed items (ItemStatus === 3)
          const pending = mappedItems.filter((i) => i.ItemStatus !== 3);
          setItems(pending);

          // init deliveryMap
          const initial = {};
          pending.forEach((item) => {
            initial[item.EstDetailId] = { DeliveringQty: 0 };
          });
          setDeliveryMap(initial);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estimateId, MisUserDetails?.OrgId, MisUserDetails?.UserId]);

  // ── helpers ───────────────────────────────────────────────────────────────
  const setQty = (id, val, maxQty) => {
    let v = parseInt(val) || 0;
    if (v < 0) v = 0;
    if (v > maxQty) v = maxQty;
    setMarkAll(false);
    setDeliveryMap((prev) => ({
      ...prev,
      [id]: { DeliveringQty: v },
    }));
  };

  const handleMarkAll = (checked) => {
    setMarkAll(checked);
    setDeliveryMap((prev) => {
      const updated = { ...prev };
      items.forEach((item) => {
        updated[item.EstDetailId] = {
          DeliveringQty: checked ? calcPending(item) : 0,
        };
      });
      return updated;
    });
  };

  const itemsTagged = Object.values(deliveryMap).filter(
    (d) => d.DeliveringQty > 0,
  ).length;

  const totalDelivering = Object.values(deliveryMap).reduce(
    (s, d) => s + (d.DeliveringQty || 0),
    0,
  );

  // ── submit ────────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    const tagged = items.filter(
      (i) => (deliveryMap[i.EstDetailId]?.DeliveringQty || 0) > 0,
    );

    if (tagged.length === 0) {
      alert("Please enter delivering quantity for at least one item.");
      return;
    }

    const payload = {
      EstimateId: parseInt(estimateId),
      DateOfTransaction: deliveryDate,
      Status: 1,
      CreatedBy: MisUserDetails?.UserId,
      Items: tagged.map((item) => ({
        EstDetailId: item.EstDetailId,
        ProductId: null,
        Quantity: deliveryMap[item.EstDetailId].DeliveringQty,
        Boxes: item.Boxes,
        VehicleNo: "",
        DriverContact: "",
      })),
    };

    try {
      const res = await fetch(`${API_BASE_URL}Billing/AddDeliveryItem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.ok) {
        alert("Delivery confirmed successfully!");
        navigate(-1);
      } else {
        alert(data?.message || data?.error || "Failed to confirm delivery.");
      }
    } catch (err) {
      console.error(err);
      alert("Network error. Please try again.");
    }
  };

  // ── loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Base>
        <div className="text-center my-5 py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-3 text-muted small">Loading quotation details...</p>
        </div>
        <Footer />
      </Base>
    );
  }

  if (!quotationData) {
    return (
      <Base>
        <div className="text-center my-5">
          <h4>No quotation data available</h4>
        </div>
        <Footer />
      </Base>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Base>
      <div className="page-header">
        <div className="page-title">
          <h3>Review Quotation</h3>
          <h6>Please verify details before confirmation</h6>
        </div>
      </div>

      <div className="px-2 pb-5 mb-5">
        {/* ── Header info card ── */}
        <div className="card mb-3 shadow-sm">
          <div className="card-body py-3">
            <p className="fw-semibold mb-1" style={{ fontSize: "15px" }}>
              {quotationData.Name}
            </p>
            <p className="text-muted small mb-2">
              <i className="fa-solid fa-phone me-1" />
              {quotationData.Mobile}
              &nbsp;&nbsp;
              <i className="fa-solid fa-location-dot me-1" />
              {quotationData.Address || "—"}
            </p>
            <div className="row row-cols-3 g-2 text-center pt-2 border-top">
              <div>
                <p className="mb-0 text-muted" style={{ fontSize: "10px" }}>
                  BILL NO
                </p>
                <p className="mb-0 fw-semibold small">{quotationData.BillNo}</p>
              </div>
              <div>
                <p className="mb-0 text-muted" style={{ fontSize: "10px" }}>
                  DATE
                </p>
                <p className="mb-0 fw-semibold small">{quotationData.Date}</p>
              </div>
              <div>
                <p className="mb-0 text-muted" style={{ fontSize: "10px" }}>
                  TYPE
                </p>
                <p className="mb-0 fw-semibold small">{quotationData.Type}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Delivery date ── */}
        <div className="card mb-3 shadow-sm">
          <div className="card-body py-3">
            <label className="form-label small fw-semibold mb-1">
              Delivery Date
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
            />
          </div>
        </div>

        {/* ── Select all + count ── */}
        <div className="d-flex justify-content-between align-items-center mb-3 ps-1">
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: "pointer" }}
            onClick={() => handleMarkAll(!markAll)}
          >
            <input
              type="checkbox"
              className="form-check-input mb-0"
              checked={markAll}
              onChange={(e) => handleMarkAll(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ width: "18px", height: "18px", accentColor: "#198754" }}
            />
            <label
              className="form-check-label small fw-medium mb-0"
              style={{ cursor: "pointer" }}
            >
              Mark All
            </label>
          </div>
          <span className="badge bg-secondary small">
            {itemsTagged} / {items.length} tagged
          </span>
        </div>

        {/* ── Item cards ── */}
        {items.map((item) => {
          const pending = calcPending(item);
          const deliveringQty =
            deliveryMap[item.EstDetailId]?.DeliveringQty || 0;
          const isTagged = deliveringQty > 0;

          return (
            <div
              key={item.EstDetailId}
              className="card mb-3 shadow-sm"
              style={{
                borderRadius: "10px",
                border: isTagged ? "2px solid #198754" : "1px solid #dee2e6",
              }}
            >
              <div className="card-body py-3 px-3">
                {/* Row 1 — item name + meta */}
                <div className="mb-2">
                  <p
                    className="fw-semibold mb-0"
                    style={{ color: "#0d6efd", fontSize: "14px" }}
                  >
                    {item.ItemDesc}
                  </p>
                  <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                    {item.Code} &nbsp;·&nbsp; {item.Size}
                    {item.Location ? ` · ${item.Location}` : ""}
                  </p>
                </div>

                {/* Row 2 — ordered / pending / delivering qty input */}
                <div className="d-flex justify-content-between align-items-end pt-2 border-top">
                  {/* left — ordered & pending */}
                  <div className="d-flex gap-3">
                    <div>
                      <p
                        className="mb-0 text-muted"
                        style={{ fontSize: "10px" }}
                      >
                        ORDERED
                      </p>
                      <p className="mb-0 fw-semibold small">
                        {item.Quantity}
                        {""}
                        {item.Unit}
                      </p>
                    </div>
                    <div>
                      <p
                        className="mb-0 text-muted"
                        style={{ fontSize: "10px" }}
                      >
                        PENDING
                      </p>
                      <p
                        className={`mb-0 fw-semibold small ${pending > 0 ? "text-warning" : "text-success"}`}
                      >
                        {pending}
                        {/* {item.Unit} */}
                      </p>
                    </div>
                  </div>

                  {/* right — delivering qty input */}
                  <div
                    className="d-flex align-items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Qty
                    </small>
                    <div
                      className="input-group input-group-sm"
                      style={{ width: "100px" }}
                    >
                      <button
                        className="btn btn-outline-secondary btn-sm px-2"
                        onClick={() =>
                          setQty(item.EstDetailId, deliveringQty - 1, pending)
                        }
                      >
                        −
                      </button>
                      <input
                        type="number"
                        className="form-control text-center px-1"
                        value={deliveringQty}
                        min={0}
                        max={pending}
                        onChange={(e) =>
                          setQty(item.EstDetailId, e.target.value, pending)
                        }
                        style={{ maxWidth: "40px" }}
                      />
                      <button
                        className="btn btn-outline-secondary btn-sm px-2"
                        onClick={() =>
                          setQty(item.EstDetailId, deliveringQty + 1, pending)
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* quick-fill link */}
                {pending > 0 && (
                  <div className="mt-1 text-end">
                    <button
                      className="btn btn-link btn-sm p-0 text-success text-decoration-none"
                      style={{ fontSize: "11px" }}
                      onClick={() => setQty(item.EstDetailId, pending, pending)}
                    >
                      Send all ({pending})
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Fixed action bar ── */}
      <div
        style={{
          position: "fixed",
          bottom: "60px",
          left: 0,
          right: 0,
          background: "white",
          borderTop: "1px solid #dee2e6",
          padding: "12px 16px",
          boxShadow: "0 -3px 12px rgba(0,0,0,0.08)",
          zIndex: 999,
        }}
      >
        {itemsTagged > 0 && (
          <p className="text-muted small mb-2 text-center">
            {itemsTagged} item(s) · {totalDelivering} units being delivered
          </p>
        )}
        <div className="d-flex gap-2 mb-4">
          <button
            className="btn btn-outline-secondary flex-grow-1"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button
            className="btn btn-success flex-grow-1 fw-semibold"
            disabled={itemsTagged === 0}
            onClick={handleConfirm}
          >
            <i className="fa-solid fa-truck-fast me-1" />
            Confirm ({itemsTagged})
          </button>
        </div>
      </div>

      <Footer />
    </Base>
  );
}
